// Curated MVP shop catalog — premium standard furnishing room sets.
// Inquiry-only. No checkout, no real prices for purchase — Richtpreise (reference prices).

// ---------------------------------------------------------------------------
// Material baseline — applied consistently across components, detail page,
// and PDF datasheets. Keep individual component `material` lines descriptive,
// but anchor them to this structured baseline.
// ---------------------------------------------------------------------------
export const MATERIAL_BASELINE = [
  { label: "Herkunftsland Holz", value: "Deutschland" },
  { label: "Holzart (botanisch)", value: "melaminharzbeschichtete Spanplatte" },
  { label: "Material", value: "FSC®-zertifizierter Holzwerkstoff" },
  { label: "Korpus", value: "FSC®-zertifizierter Holzwerkstoff" },
  { label: "Türen", value: "FSC®-zertifizierter Holzwerkstoff" },
  { label: "Schubladen", value: "FSC®-zertifizierter Holzwerkstoff" },
  { label: "Auszüge", value: "Metall" },
  { label: "Beschläge", value: "Metall" },
  { label: "Füße", value: "Massivholz" },
  { label: "Griffe", value: "Holz" },
];

export const ROOMS = [
  { key: "wohnzimmer", label: "Wohnzimmer" },
  { key: "schlafzimmer", label: "Schlafzimmer" },
  { key: "essbereich", label: "Essbereich" },
  { key: "flur", label: "Flur & Garderobe" },
  { key: "apartment", label: "Apartment" },
  { key: "refit", label: "Refit" },
  { key: "bad", label: "Badmöbel" },
];

export const STYLE_FILTERS = [
  { key: "kueste", label: "Küste" },
  { key: "alpin", label: "Alpin" },
  { key: "skandi", label: "Skandi" },
  { key: "landhaus-modern", label: "Landhaus Modern" },
  { key: "urban-retreat", label: "Urban Retreat" },
];

export const AUDIENCE_FILTERS = [
  { key: "privat", label: "Privat" },
  { key: "gewerblich", label: "Gewerblich" },
  { key: "beide", label: "Beide" },
];

// Reference price buckets (in EUR). Match against priceFrom.
export const PRICE_BUCKETS = [
  { key: "lt-3k", label: "bis 3.000 €", min: 0, max: 2999 },
  { key: "3-6k", label: "3.000 – 6.000 €", min: 3000, max: 5999 },
  { key: "6-9k", label: "6.000 – 9.000 €", min: 6000, max: 8999 },
  { key: "gt-9k", label: "ab 9.000 €", min: 9000, max: 1_000_000 },
];

export const SORT_OPTIONS = [
  { key: "recommended", label: "Empfehlung" },
  { key: "price-asc", label: "Preis aufsteigend" },
  { key: "price-desc", label: "Preis absteigend" },
  { key: "newest", label: "Neu" },
];

export const formatEur = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

const c = (title, desc, material, finish, dimensions, note) => ({
  title,
  desc,
  material,
  finish,
  dimensions,
  note,
});

export const ROOM_SETS = [
  {
    slug: "wohnzimmer-skandi",
    name: "Wohnzimmer · Skandi",
    room: "wohnzimmer",
    style: "skandi",
    style_label: "Skandi",
    subtitle: "Helle Klarheit für entspannte Aufenthalte.",
    description:
      "Ein vollständig komponiertes Wohnzimmer in skandinavischer Handschrift: warme Hölzer, weiche Bouclé-Stoffe, ruhige Linien. Funktioniert in Ferienwohnungen ebenso wie in dauerhaft genutzten Apartments.",
    suitedFor: ["Ferienwohnung", "Apartment", "Privat", "Gewerblich"],
    audience: "beide",
    priceFrom: 6490,
    priceTo: 8590,
    createdAt: "2025-09-10",
    image:
      "https://images.unsplash.com/photo-1554995207-c18c203602cb?auto=format&fit=crop&w=1600&q=80",
    palette: ["#F3EFE7", "#D9CFBE", "#7C7468", "#1A1A1A"],
    components: [
      c("Sofa, 3-sitzig", "Klassisches 3-Sitzer-Sofa mit weichem Bouclé-Bezug, ruhige Sitzkante.", "Bouclé 100% Polyester recycelt, Hartholzgestell", "Natur · Esche weiß geölt", "B 220 · T 95 · H 82 cm", "Bezug abnehmbar, reinigungsfreundlich"),
      c("Couchtisch", "Niedriger Tisch mit kreisrunder Platte, klare Linie.", "Massive Esche, geölt", "Esche weiß geölt", "Ø 95 · H 38 cm", "Lichtbeständige Oberfläche"),
      c("Sideboard, 3-türig", "Stauraum für Wohnzimmer-Equipment, leise Push-to-open Türen.", "FSC®-zertifizierter Holzwerkstoff, Echtholzfurnier Esche", "Esche natur", "B 180 · T 42 · H 78 cm", "3 Innenfächer mit Glasböden"),
      c("TV-Lowboard, schwebend", "Wandhängendes Lowboard mit zwei Klappen, Kabelmanagement.", "FSC®-zertifizierter Holzwerkstoff, Echtholzfurnier", "Esche natur", "B 160 · T 38 · H 32 cm", "Inkl. Wandhalterung"),
      c("Hochregal, asymmetrisch", "Offenes Regal mit asymmetrischer Fachung.", "Massive Esche", "Esche natur", "B 90 · T 35 · H 200 cm", "Wandsicherung im Lieferumfang"),
      c("Sessel mit Holzrahmen", "Lounge-Sessel mit gepolsterter Sitzfläche.", "Bouclé, Massivholz", "Natur · Esche", "B 78 · T 82 · H 80 cm", "Ergonomische Sitzhöhe 44 cm"),
      c("Beistelltisch, rund", "Kleiner runder Beistelltisch.", "Massive Esche", "Esche natur", "Ø 45 · H 50 cm", "Nachhaltige Holzherkunft"),
      c("Lampen- & Dekorpaket", "3 Leuchten + abgestimmtes Vasen-Set.", "Keramik, Stahl pulverbeschichtet", "Sand, Schwarz matt", "Pendel Ø 35, Steh H 158, Tisch H 42 cm", "LED, dimmbar"),
    ],
  },
  {
    slug: "wohnzimmer-urban-retreat",
    name: "Wohnzimmer · Urban Retreat",
    room: "wohnzimmer",
    style: "urban-retreat",
    style_label: "Urban Retreat",
    subtitle: "Dichte Materialität für den urbanen Rückzug.",
    description:
      "Dunklere Nuancen, dichte Texturen, präzise Linienführung. Ein Wohnraum, der Ruhe im urbanen Kontext herstellt — ideal für Stadtapartments und Serviced-Formate.",
    suitedFor: ["Apartment", "Serviced Apartment", "Privat", "Gewerblich"],
    audience: "beide",
    priceFrom: 7890,
    priceTo: 8590,
    createdAt: "2025-10-22",
    image:
      "https://images.pexels.com/photos/4409370/pexels-photo-4409370.jpeg?auto=compress&cs=tinysrgb&w=1600",
    palette: ["#EAE5DC", "#807568", "#3C352D", "#15130F"],
    components: [
      c("Sofa, 3-sitzig", "Tief gepolstertes 3-Sitzer-Sofa, dichte Mohair-Optik.", "Mohair-Gewebe, Hartholzgestell", "Taupe · matt", "B 230 · T 100 · H 80 cm", "Lehne in zwei Höhen einstellbar"),
      c("Couchtisch", "Niedriger Tisch mit brünierter Bronze-Platte.", "Bronze brüniert, Stahl", "Bronze · Schwarz matt", "B 110 · T 60 · H 35 cm", "Pflegeleichte Patina"),
      c("Sideboard", "Sideboard in Räuchereiche mit grifflosen Klappen.", "Massive Eiche, geräuchert", "Räuchereiche", "B 200 · T 45 · H 80 cm", "Soft-Close Beschläge"),
      c("Sessel, Leder cognac", "Lounge-Sessel mit pflanzlich gegerbtem Leder.", "Vollnarbleder, Stahlrahmen", "Cognac · Schwarz matt", "B 80 · T 84 · H 78 cm", "Patiniert mit der Zeit"),
      c("Wandregal, modular", "Modulares Regalsystem, frei kombinierbar.", "Stahl, Räuchereiche", "Schwarz matt · Räuchereiche", "B 240 · T 30 · H 220 cm", "Wandfixierung im Set"),
      c("Stehleuchte", "Hohe Stehleuchte mit gerichtetem Licht.", "Stahl pulverbeschichtet, Textilkabel", "Schwarz matt", "Ø 35 · H 175 cm", "Dimmbar, E27 LED"),
      c("Beistelltisch & Tablett-Set", "Beistelltisch plus 2 Tabletts.", "Stahl, Eichenfurnier", "Schwarz · Eiche", "Ø 40 · H 55 cm", "Tabletts spülmaschinenfest"),
      c("Textil-Set", "Vorhang, Teppich, 4 Kissen, abgestimmt.", "Wolle, Leinen, Baumwolle", "Taupe / Anthrazit", "Teppich 240 × 300 cm", "Schadstoffgeprüft"),
    ],
  },
  {
    slug: "schlafzimmer-alpin",
    name: "Schlafzimmer · Alpin",
    room: "schlafzimmer",
    style: "alpin",
    style_label: "Alpin",
    subtitle: "Ehrliche Hölzer, ruhiger Schlaf.",
    description:
      "Eine moderne, klare Lesart alpiner Architektur. Massive Hölzer, präzise Verarbeitung, warme Wollfilz-Akzente — kein Chalet-Klischee.",
    suitedFor: ["Ferienhaus", "Refit", "Privat"],
    audience: "privat",
    priceFrom: 6290,
    priceTo: 7490,
    createdAt: "2025-08-04",
    image:
      "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1600&q=80",
    palette: ["#EDE6D9", "#A89478", "#5C4A36", "#1A1816"],
    components: [
      c("Doppelbett 180 × 200", "Massiv-Bett mit niedrigem Kopfteil aus Lärche.", "Massive Lärche, geräuchert", "Lärche geräuchert", "B 200 · L 220 · H 90 cm", "Lattenrost optional"),
      c("Kleiderschrank, 4-türig", "Hoher Schrank mit Innenausstattung.", "Massivholz, Innenstoff Wollfilz", "Lärche geräuchert", "B 240 · T 62 · H 220 cm", "2 Kleiderstangen, 4 Schübe"),
      c("Nachttische (2 Stück)", "Kompakter Nachttisch mit einer Schublade.", "Massive Lärche", "Lärche geräuchert", "B 50 · T 40 · H 55 cm", "Kabelausgang an der Rückwand"),
      c("Regalboard, wandhängend", "Wandhängendes Board mit Lederzügeln.", "Massivholz, Pflanzleder", "Lärche · Naturleder", "B 120 · T 22 · H 30 cm", "Lederpatina entwickelt sich"),
      c("Kommode, breit", "Sechs-Schub-Kommode für Wäsche.", "Massivholz, Schmiedestahlgriffe", "Lärche · Schwarz matt", "B 160 · T 48 · H 88 cm", "Soft-Close Vollauszüge"),
      c("Bank am Fußende", "Sitzbank mit Wollfilz-Auflage.", "Holzgestell, Wollfilz", "Lärche · Naturwolle", "B 160 · T 42 · H 45 cm", "Auflage abnehmbar"),
      c("Textil-Paket", "Bettwäsche-Set, Plaid und Teppich.", "Leinen, Wolle, Baumwolle", "Sand / Anthrazit", "Teppich 200 × 300 cm", "Hypoallergen, Öko-Tex"),
    ],
  },
  {
    slug: "schlafzimmer-kueste",
    name: "Schlafzimmer · Küste",
    room: "schlafzimmer",
    style: "kueste",
    style_label: "Küste",
    subtitle: "Licht, Leinen, weite Morgen.",
    description:
      "Gebleichte Hölzer und Leinenstrukturen für ein Schlafzimmer, das den Tag ruhig beginnt. Ideal für Ferienhäuser und Ferienwohnungen am Wasser.",
    suitedFor: ["Ferienhaus", "Ferienwohnung", "Privat", "Gewerblich"],
    audience: "beide",
    priceFrom: 4990,
    priceTo: 7490,
    createdAt: "2025-09-25",
    image:
      "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&w=1600&q=80",
    palette: ["#EFE9DF", "#CDC2AE", "#8A8778", "#1F1E1C"],
    components: [
      c("Doppelbett 160 × 200", "Bett mit gepolstertem Kopfteil in Leinen.", "Massive Eiche gekalkt, Leinenpolster", "Eiche gekalkt · Natur", "B 180 · L 215 · H 110 cm", "Bezug abnehmbar"),
      c("Kleiderschrank 3-türig", "Schrank mit Stoffinlay an den Türen.", "FSC®-zertifizierter Holzwerkstoff, Echtholzfurnier, Stoff", "Eiche gekalkt · Sand", "B 180 · T 60 · H 220 cm", "1 Kleiderstange, 6 Fächer"),
      c("Nachttische, schmal (2 Stück)", "Schmaler Nachttisch mit offener Ablage.", "Massive Eiche", "Eiche gekalkt", "B 40 · T 36 · H 55 cm", "Standfest auch auf Teppich"),
      c("Sitzbank, Leinen natur", "Bank mit weicher Leinen-Polsterung.", "Holzgestell, Leinen", "Eiche gekalkt · Natur", "B 140 · T 38 · H 45 cm", "Bezug waschbar bis 30 °C"),
      c("Kommode mit Korbschüben", "Kommode mit 4 herausnehmbaren Körben.", "Massivholz, Naturfaserkörbe", "Eiche gekalkt · Natur", "B 120 · T 45 · H 90 cm", "Körbe einzeln nachbestellbar"),
      c("Spiegel, gerundet", "Großer Wandspiegel mit weichen Kanten.", "Float-Glas, Massivholzrahmen", "Eiche gekalkt", "B 70 · H 110 cm", "Inkl. Wandhalterung"),
      c("Textil-Paket", "Vorhänge, Bettwäsche, Teppich.", "Leinen, Baumwolle", "Natur / Sand", "Teppich 200 × 290 cm", "Vorgewaschen"),
    ],
  },
  {
    slug: "schlafzimmer-landhaus-modern",
    name: "Schlafzimmer · Landhaus Modern",
    room: "schlafzimmer",
    style: "landhaus-modern",
    style_label: "Landhaus Modern",
    subtitle: "Handwerk, neu gelesen.",
    description:
      "Grobe Strukturen, handwerkliche Details, ruhige Flächen. Ein Schlafzimmer mit Charakter — ohne Nostalgie, ohne Klischee.",
    suitedFor: ["Ferienhaus", "Refit", "Privat"],
    audience: "privat",
    priceFrom: 6890,
    priceTo: 7490,
    createdAt: "2025-07-18",
    image:
      "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1600&q=80",
    palette: ["#E9E3D4", "#B8A683", "#4E4537", "#1A1A1A"],
    components: [
      c("Doppelbett 180 × 200", "Bett mit massivem astigem Eichenrahmen.", "Massive Eiche, astig", "Eiche natur", "B 200 · L 220 · H 95 cm", "Sichtbare Naturmerkmale"),
      c("Kleiderschrank 4-türig", "Schrank mit Schmiedestahl-Beschlägen.", "Massivholz, Schmiedestahl", "Eiche · Schwarz matt", "B 220 · T 62 · H 220 cm", "Innen mit Hutfach"),
      c("Nachttische (2 Stück)", "Tisch mit Marmor-Auflage.", "Massivholz, Carrara-Marmor", "Eiche · Marmor weiß", "B 45 · T 40 · H 58 cm", "Marmor naturversiegelt"),
      c("Kommode, breit", "Sechs-Schub-Kommode mit handgeführten Griffen.", "Massivholz, Schmiedestahlgriffe", "Eiche · Schwarz matt", "B 160 · T 48 · H 88 cm", "Vollauszüge"),
      c("Sitzbank, Baumwollgewebe", "Bank mit grobem Naturgewebe.", "Holzgestell, Baumwolle", "Eiche · Natur", "B 160 · T 40 · H 45 cm", "Bezug abnehmbar"),
      c("Pendelleuchten-Set (2 Stück)", "Zwei Pendel über die Nachttische.", "Stahl, Glasopal", "Schwarz matt · Opalweiß", "Ø 22 · H variabel", "LED E27 inklusive"),
      c("Textil-Paket", "Bettwäsche, Plaid, Teppich.", "Leinen, Wolle", "Sand / Tabak", "Teppich 200 × 300 cm", "Naturfasern"),
    ],
  },
  {
    slug: "essbereich-skandi",
    name: "Essbereich · Skandi",
    room: "essbereich",
    style: "skandi",
    style_label: "Skandi",
    subtitle: "Klar gedeckter Raum für Gemeinsames.",
    description:
      "Ein konzentriertes Esszimmer-Set in skandinavischer Klarheit — heller Tisch, weiche Stuhl-Linien, durchdachte Aufbewahrung.",
    suitedFor: ["Ferienhaus", "Ferienwohnung", "Privat", "Gewerblich"],
    audience: "beide",
    priceFrom: 3690,
    priceTo: 5190,
    createdAt: "2025-10-02",
    image:
      "https://images.unsplash.com/photo-1615876234886-fd9a39fda97f?auto=format&fit=crop&w=1600&q=80",
    palette: ["#F3EFE7", "#D9CFBE", "#7C7468", "#1A1A1A"],
    components: [
      c("Esstisch 200 × 95 cm", "Rechteckiger Tisch mit weicher Kantenfase.", "Massive Esche", "Esche weiß geölt", "B 200 · T 95 · H 76 cm", "Verlängerung optional"),
      c("Stühle (6 Stück)", "Stuhl mit gepolstertem Sitz.", "Bouclé, Massivholz", "Natur · Esche", "B 48 · T 52 · H 84 cm", "Stapelbar bis 4 Stück"),
      c("Sideboard 3-türig", "Aufbewahrung für Geschirr.", "FSC®-zertifizierter Holzwerkstoff, Echtholzfurnier", "Esche natur", "B 180 · T 42 · H 78 cm", "Höhenverstellbare Böden"),
      c("Wandregal, schmal", "Schmales Regal über dem Sideboard.", "Massive Esche", "Esche natur", "B 120 · T 22 · H 30 cm", "Wandfixierung im Set"),
      c("Pendelleuchte, 3-flammig", "Pendel über dem Esstisch.", "Stahl, Opalglas", "Schwarz matt · Opal", "B 110 · H variabel", "LED E14 inklusive"),
      c("Geschirr- & Glas-Set", "Vollausstattung für 12 Personen.", "Steinzeug, Glas", "Sand · Klar", "div.", "Spülmaschinen- und mikrowellenfest"),
    ],
  },
  {
    slug: "essbereich-kueste",
    name: "Essbereich · Küste",
    room: "essbereich",
    style: "kueste",
    style_label: "Küste",
    subtitle: "Lange Mittage am hellen Tisch.",
    description:
      "Gebleichte Eiche, Leinenstühle, helle Keramik — ein Essbereich, der Sonne, Salz und Sand verträgt.",
    suitedFor: ["Ferienhaus", "Ferienwohnung", "Privat"],
    audience: "privat",
    priceFrom: 4290,
    priceTo: 5190,
    createdAt: "2025-08-30",
    image:
      "https://images.unsplash.com/photo-1541123437800-1bb1317badc2?auto=format&fit=crop&w=1600&q=80",
    palette: ["#EFE9DF", "#CDC2AE", "#8A8778", "#1F1E1C"],
    components: [
      c("Esstisch 220 × 100 cm", "Großer Tisch für entspannte Mittage.", "Massive Eiche gekalkt", "Eiche gekalkt", "B 220 · T 100 · H 76 cm", "Kantenfase 5°"),
      c("Stühle (6 Stück)", "Leinenstuhl mit weichem Sitzkissen.", "Massivholz, Leinen", "Natur", "B 46 · T 52 · H 86 cm", "Bezug abnehmbar"),
      c("Sideboard, breit", "Aufbewahrung mit Glaseinsätzen.", "Massivholz, Float-Glas", "Eiche gekalkt", "B 200 · T 45 · H 80 cm", "Beleuchtung optional"),
      c("Wandregal mit Hakenleiste", "Regal mit integrierten Haken.", "Massivholz, Messing brüniert", "Eiche gekalkt · Messing", "B 140 · T 22 · H 30 cm", "5 Haken"),
      c("Pendelleuchte, Rattan", "Geflochtene Pendelleuchte.", "Naturrattan, Stahl", "Natur · Schwarz matt", "Ø 55 · H variabel", "Handgefertigt"),
      c("Geschirr- & Keramik-Set sand", "Vollausstattung für 8 Personen.", "Steinzeug glasiert", "Sand", "div.", "Reaktiv glasierte Oberfläche"),
    ],
  },
  {
    slug: "flur-garderobe-skandi",
    name: "Flur & Garderobe · Skandi",
    room: "flur",
    style: "skandi",
    style_label: "Skandi",
    subtitle: "Erster Eindruck, ruhig komponiert.",
    description:
      "Ein durchdachtes Garderoben-Set für klare Eingangssituationen — Stauraum, Sitz, Spiegel und Licht in einer Komposition.",
    suitedFor: ["Ferienhaus", "Ferienwohnung", "Apartment", "Privat", "Gewerblich"],
    audience: "beide",
    priceFrom: 2290,
    priceTo: 2890,
    createdAt: "2025-11-12",
    image:
      "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=1600&q=80",
    palette: ["#F3EFE7", "#D9CFBE", "#7C7468", "#1A1A1A"],
    components: [
      c("Garderobenwand", "Hakenleiste mit Regal über Kopfhöhe.", "Massive Esche, Schmiedestahl", "Esche · Schwarz matt", "B 140 · T 30 · H 200 cm", "5 Doppelhaken"),
      c("Sitzbank mit Schuhfach", "Bank mit offenem Schuhfach.", "Massivholz, Naturfaserkörbe", "Esche · Natur", "B 120 · T 38 · H 45 cm", "2 Körbe inklusive"),
      c("Wandspiegel gerundet", "Spiegel mit weichen Kanten.", "Float-Glas, Holzrahmen", "Esche", "B 60 · H 80 cm", "Sicherheitsglas"),
      c("Schlüsselboard", "Kompaktes Board mit Hakenleiste.", "Massivholz", "Esche", "B 30 · T 8 · H 22 cm", "5 Haken"),
      c("Wandleuchte", "Indirekte LED-Beleuchtung.", "Stahl, Opalglas", "Schwarz matt", "B 8 · H 30 cm", "Warmweiß 2700K"),
      c("Schirmständer", "Schmaler Schirmständer.", "Stahl, Filzboden", "Schwarz matt", "Ø 22 · H 55 cm", "Tropfwanne integriert"),
    ],
  },
  {
    slug: "apartment-starter",
    name: "Apartment Starter Set",
    room: "apartment",
    style: "skandi",
    style_label: "Skandi",
    subtitle: "Ein vollständiges Apartment, schlüsselfertig.",
    description:
      "Das kompakte Komplett-Set für kleinere Apartments und Studios. Vom Bett bis zum Esstisch — alles wesentliche, ruhig aufeinander abgestimmt.",
    suitedFor: ["Apartment", "Serviced Apartment", "Privat", "Gewerblich"],
    audience: "beide",
    priceFrom: 11890,
    priceTo: 14890,
    createdAt: "2025-12-05",
    image:
      "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1600&q=80",
    palette: ["#F3EFE7", "#D9CFBE", "#7C7468", "#1A1A1A"],
    components: [
      c("Bett 140 × 200", "Bett mit reduziertem Kopfteil.", "Massive Esche, Bouclé", "Esche · Natur", "B 160 · L 215 · H 95 cm", "Lattenrost optional"),
      c("Kleiderschrank 2-türig", "Schmaler Schrank, ideal für Apartments.", "FSC®-zertifizierter Holzwerkstoff, Echtholzfurnier", "Esche natur", "B 120 · T 60 · H 220 cm", "Hutfach + Kleiderstange"),
      c("Schreibtisch / Konsole", "Multifunktionale Konsole, auch für Home-Office.", "Massivholz", "Esche natur", "B 140 · T 50 · H 75 cm", "Kabelausgang"),
      c("Esstisch klein, 2 Stühle", "Kompakter Tisch mit zwei Stühlen.", "Massivholz, Bouclé", "Esche · Natur", "B 110 · T 70 · H 76 cm", "Stühle stapelbar"),
      c("2-Sitzer Sofa oder Sessel", "Variante je nach Grundriss wählbar.", "Bouclé, Hartholzgestell", "Natur · Esche", "B 170 · T 90 · H 80 cm", "Bezug abnehmbar"),
      c("Sideboard / Stauraum", "Sideboard mit zwei Türen und einer Schublade.", "FSC®-zertifizierter Holzwerkstoff, Echtholzfurnier", "Esche natur", "B 140 · T 40 · H 78 cm", "Soft-Close"),
      c("Licht-Basis", "Pendel + Stehleuchte + Tischleuchte.", "Stahl, Opalglas", "Schwarz matt · Opal", "div.", "LED inklusive"),
      c("Textil-Basis", "Bettwäsche, Vorhänge, Teppich.", "Leinen, Baumwolle", "Natur / Sand", "Teppich 200 × 290 cm", "Öko-Tex zertifiziert"),
    ],
  },
  {
    slug: "refit-refresh",
    name: "Refit Refresh Set",
    room: "refit",
    style: "landhaus-modern",
    style_label: "Landhaus Modern",
    subtitle: "Bestand klüger machen.",
    description:
      "Eine kuratierte Auswahl an Austausch-Möbeln, Textilien und Leuchten — gedacht, um ein bestehendes Ferienobjekt visuell und funktional zu modernisieren, ohne alles neu zu bauen.",
    suitedFor: ["Ferienhaus", "Ferienwohnung", "Refit", "Privat", "Gewerblich"],
    audience: "beide",
    priceFrom: 8490,
    priceTo: 12490,
    createdAt: "2025-11-28",
    image:
      "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1600&q=80",
    palette: ["#E9E3D4", "#B8A683", "#4E4537", "#1A1A1A"],
    components: [
      c("Statement-Sessel (2 Stück)", "Markante Sessel als visueller Reset.", "Wolle, Massivholz", "Tabak · Eiche", "B 80 · T 84 · H 80 cm", "Drehfunktion optional"),
      c("Wohnzimmer-Teppich, groß", "Großformatiger Teppich, raumdefinierend.", "Wolle, Pflanzenfaser", "Sand / Tabak", "240 × 340 cm", "Naturfasern"),
      c("Sideboard, Bestands-Ersatz", "Drop-in Sideboard für Standardflächen.", "Massivholz, Schmiedestahl", "Eiche · Schwarz matt", "B 200 · T 45 · H 80 cm", "Soft-Close"),
      c("Pendel- & Wandleuchten-Set", "Vier Leuchten für Wohnen und Essen.", "Stahl, Opalglas", "Schwarz matt · Opal", "div.", "LED dimmbar"),
      c("Textil-Paket", "Vorhänge, 4 Plaids, 8 Kissen.", "Wolle, Leinen", "Sand / Anthrazit / Tabak", "div.", "Schadstoffgeprüft"),
      c("Spiegel & Garderoben-Akzente", "Aufwertung Eingangssituation.", "Glas, Massivholz, Messing", "Eiche · Messing", "div.", "Inkl. Wandhalterungen"),
      c("Akzent- & Beistelltische", "Drei Tische in unterschiedlichen Höhen.", "Massivholz, Stahl", "Eiche · Schwarz matt", "div.", "Stapelbar"),
    ],
  },

  // ---- Iteration 8 · Catalog Extension (10 new sets, incl. Badmöbel) ----
  {
    slug: "wohnzimmer-kueste",
    name: "Wohnzimmer · Küste",
    room: "wohnzimmer",
    style: "kueste",
    style_label: "Küste",
    subtitle: "Helle Räume, salzige Brise, weiche Linien.",
    description:
      "Ein Wohnzimmer-Set für Objekte am Wasser: gebleichte Hölzer, Leinen-Texturen, Rattan-Akzente. Wirkt großzügig auch in mittleren Grundrissen — und verzeiht Sand.",
    suitedFor: ["Ferienhaus", "Ferienwohnung", "Privat", "Gewerblich"],
    audience: "beide",
    priceFrom: 6890,
    priceTo: 8590,
    createdAt: "2026-01-12",
    image:
      "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?auto=format&fit=crop&w=1600&q=80",
    palette: ["#EFE9DF", "#CDC2AE", "#8A8778", "#1F1E1C"],
    components: [
      c("Sofa, 3-sitzig", "Sofa mit weichem Leinenbezug und ruhiger Sitzkante.", "Leinen, Hartholzgestell", "Natur · Eiche gekalkt", "B 220 · T 95 · H 82 cm", "Bezug abnehmbar, vorgewaschen"),
      c("Couchtisch", "Niedriger Tisch mit ovaler Platte.", "Massive Eiche gekalkt", "Eiche gekalkt", "B 120 · T 65 · H 38 cm", "Lichtbeständig"),
      c("Sideboard, breit", "Aufbewahrung mit grifflosen Türen.", "FSC®-zertifizierter Holzwerkstoff, Echtholzfurnier", "Eiche gekalkt", "B 200 · T 42 · H 78 cm", "Soft-Close"),
      c("Sessel, Rattan", "Lounge-Sessel mit Rattan-Geflecht.", "Naturrattan, Massivholz", "Natur · Eiche gekalkt", "B 76 · T 80 · H 80 cm", "Handgeflochten"),
      c("Wandregal, schmal", "Wandregal mit drei Ebenen.", "Massivholz", "Eiche gekalkt", "B 140 · T 22 · H 90 cm", "Wandfixierung im Set"),
      c("Beistelltisch, rund", "Kleiner Tisch mit Geflecht-Korpus.", "Rattan, Massivholz", "Natur", "Ø 45 · H 50 cm", "Naturmaterial"),
      c("Lampen-Set Rattan", "2 Pendel + 1 Stehleuchte.", "Naturrattan, Stahl", "Natur · Schwarz matt", "Pendel Ø 50 · Steh H 165 cm", "LED E27 inklusive"),
      c("Textil-Paket", "Vorhang, Teppich, 4 Kissen.", "Leinen, Baumwolle, Wolle", "Natur / Sand", "Teppich 240 × 300 cm", "Öko-Tex"),
    ],
  },
  {
    slug: "wohnzimmer-alpin",
    name: "Wohnzimmer · Alpin",
    room: "wohnzimmer",
    style: "alpin",
    style_label: "Alpin",
    subtitle: "Massive Hölzer, ehrliche Stoffe, dichte Ruhe.",
    description:
      "Ein Wohnzimmer in moderner alpiner Lesart: massive Lärche, Wollfilz, Lederakzente. Funktioniert in Berghütten, Chaletwohnungen und renovierten Bestandsobjekten gleichermaßen.",
    suitedFor: ["Ferienhaus", "Refit", "Privat"],
    audience: "privat",
    priceFrom: 7490,
    priceTo: 8590,
    createdAt: "2025-12-18",
    image:
      "https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=1600&q=80",
    palette: ["#EDE6D9", "#A89478", "#5C4A36", "#1A1816"],
    components: [
      c("Sofa, 3-sitzig", "Sofa mit Wollfilz-Bezug und tiefer Sitzfläche.", "Wollfilz, Hartholzgestell", "Sand · Lärche geräuchert", "B 230 · T 100 · H 82 cm", "Bezug abnehmbar"),
      c("Couchtisch", "Massiver Tisch mit sichtbarer Maserung.", "Massive Lärche", "Lärche geräuchert", "B 130 · T 65 · H 38 cm", "Naturöl-Finish"),
      c("Sideboard, massiv", "Sideboard mit Schmiedestahl-Griffen.", "Massivholz, Schmiedestahl", "Lärche · Schwarz matt", "B 200 · T 45 · H 80 cm", "Soft-Close Vollauszüge"),
      c("Sessel, Pflanzleder", "Lounge-Sessel, patiniert mit der Zeit.", "Pflanzleder, Massivholz", "Tabak · Lärche", "B 78 · T 84 · H 80 cm", "Lederpatina entwickelt sich"),
      c("Hochregal", "Asymmetrisches Hochregal als Raum-Anker.", "Massivholz", "Lärche geräuchert", "B 100 · T 35 · H 220 cm", "Wandsicherung im Set"),
      c("Stehleuchte, Filz", "Stehleuchte mit Wollfilz-Schirm.", "Stahl, Wollfilz", "Schwarz matt · Sand", "Ø 38 · H 168 cm", "Dimmbar, LED E27"),
      c("Beistelltisch", "Kleiner Beistelltisch.", "Massivholz", "Lärche", "Ø 42 · H 52 cm", "FSC-zertifiziert"),
      c("Textil-Paket", "Vorhang, Teppich, 4 Kissen, Plaid.", "Wolle, Leinen", "Sand / Tabak", "Teppich 240 × 300 cm", "Schadstoffgeprüft"),
    ],
  },
  {
    slug: "wohnzimmer-landhaus-modern",
    name: "Wohnzimmer · Landhaus Modern",
    room: "wohnzimmer",
    style: "landhaus-modern",
    style_label: "Landhaus Modern",
    subtitle: "Handwerkliche Substanz, klar komponiert.",
    description:
      "Grobe Naturstoffe, präzise Schmiedestahl-Beschläge, ruhige Holzflächen. Ein Wohnzimmer mit Charakter — ohne Nostalgie, ohne Folklore.",
    suitedFor: ["Ferienhaus", "Refit", "Privat", "Gewerblich"],
    audience: "beide",
    priceFrom: 7290,
    priceTo: 8590,
    createdAt: "2026-01-22",
    image:
      "https://images.unsplash.com/photo-1538688525198-9b88f6f53126?auto=format&fit=crop&w=1600&q=80",
    palette: ["#E9E3D4", "#B8A683", "#4E4537", "#1A1A1A"],
    components: [
      c("Sofa, 3-sitzig", "Sofa mit grobem Naturgewebe.", "Wolle, Hartholzgestell", "Natur · Eiche astig", "B 230 · T 95 · H 82 cm", "Bezug abnehmbar"),
      c("Couchtisch", "Massiver Tisch mit astigem Eichenholz.", "Massive Eiche", "Eiche natur", "B 130 · T 70 · H 38 cm", "Naturmerkmale sichtbar"),
      c("Sideboard, Schmiedeeisen", "Sideboard mit handgeführten Beschlägen.", "Massivholz, Schmiedestahl", "Eiche · Schwarz matt", "B 200 · T 45 · H 80 cm", "Vollauszüge"),
      c("Sessel, Leinen grob", "Lounge-Sessel mit Leinen-Polster.", "Leinen, Massivholz", "Natur · Eiche", "B 80 · T 84 · H 80 cm", "Bezug abnehmbar"),
      c("Wandregal mit Marmor", "Regal mit Marmor-Auflage.", "Massivholz, Carrara-Marmor", "Eiche · Marmor weiß", "B 150 · T 25 · H 120 cm", "Wandfixierung im Set"),
      c("Pendelleuchten (2 Stück)", "Pendel mit Opalglas.", "Stahl, Opalglas", "Schwarz matt · Opal", "Ø 24 · H variabel", "LED E27"),
      c("Beistelltische (Set 2)", "Zwei stapelbare Tische.", "Massivholz, Stahl", "Eiche · Schwarz matt", "Ø 40/45 · H 48/55 cm", "Stapelbar"),
      c("Textil-Paket", "Teppich, 4 Kissen, Plaid, Vorhang.", "Wolle, Leinen", "Sand / Tabak / Anthrazit", "Teppich 240 × 320 cm", "Naturfasern"),
    ],
  },
  {
    slug: "schlafzimmer-skandi",
    name: "Schlafzimmer · Skandi",
    room: "schlafzimmer",
    style: "skandi",
    style_label: "Skandi",
    subtitle: "Ruhige Helle für klare Morgen.",
    description:
      "Skandinavisches Schlafzimmer-Set in heller Esche und weichem Leinen. Reduziert, gut sortiert, mit ehrlichem Stauraum — passend zu Ferienwohnungen und Apartments.",
    suitedFor: ["Ferienwohnung", "Apartment", "Privat", "Gewerblich"],
    audience: "beide",
    priceFrom: 5290,
    priceTo: 7490,
    createdAt: "2025-12-30",
    image:
      "https://images.unsplash.com/photo-1540518614846-7eded433c457?auto=format&fit=crop&w=1600&q=80",
    palette: ["#F3EFE7", "#D9CFBE", "#7C7468", "#1A1A1A"],
    components: [
      c("Doppelbett 160 × 200", "Bett mit gepolstertem Leinen-Kopfteil.", "Massive Esche, Leinen", "Esche natur · Natur", "B 180 · L 215 · H 100 cm", "Bezug abnehmbar"),
      c("Kleiderschrank 3-türig", "Schrank mit Kleiderstange und 6 Fächern.", "FSC®-zertifizierter Holzwerkstoff, Echtholzfurnier", "Esche natur", "B 180 · T 60 · H 220 cm", "Soft-Close"),
      c("Nachttische (2 Stück)", "Nachttisch mit einer Schublade.", "Massive Esche", "Esche natur", "B 45 · T 38 · H 55 cm", "Kabelausgang an Rückwand"),
      c("Kommode, breit", "Sechs-Schub-Kommode.", "FSC®-zertifizierter Holzwerkstoff, Echtholzfurnier", "Esche natur", "B 160 · T 48 · H 88 cm", "Soft-Close Vollauszüge"),
      c("Sitzbank am Fußende", "Bank mit Bouclé-Auflage.", "Massivholz, Bouclé", "Esche · Natur", "B 140 · T 38 · H 45 cm", "Auflage abnehmbar"),
      c("Spiegel, gerundet", "Wandspiegel mit weichen Kanten.", "Float-Glas, Holzrahmen", "Esche natur", "B 65 · H 100 cm", "Sicherheitsglas"),
      c("Textil-Paket", "Bettwäsche, Vorhänge, Teppich.", "Leinen, Baumwolle", "Natur / Sand", "Teppich 200 × 290 cm", "Öko-Tex"),
    ],
  },
  {
    slug: "schlafzimmer-urban-retreat",
    name: "Schlafzimmer · Urban Retreat",
    room: "schlafzimmer",
    style: "urban-retreat",
    style_label: "Urban Retreat",
    subtitle: "Dichte Stille für die Stadt.",
    description:
      "Schlafzimmer-Set für urbane Apartments und Serviced-Formate: Räuchereiche, Mohair-Optik, Bronze-Akzente. Beruhigt den Raum visuell, ohne dunkel zu wirken.",
    suitedFor: ["Apartment", "Serviced Apartment", "Privat", "Gewerblich"],
    audience: "beide",
    priceFrom: 6890,
    priceTo: 7490,
    createdAt: "2026-01-04",
    image:
      "https://images.unsplash.com/photo-1616594039964-ae9021a400a0?auto=format&fit=crop&w=1600&q=80",
    palette: ["#EAE5DC", "#807568", "#3C352D", "#15130F"],
    components: [
      c("Doppelbett 180 × 200", "Bett mit Mohair-Kopfteil und Räuchereiche-Rahmen.", "Massive Eiche geräuchert, Mohair", "Räuchereiche · Taupe", "B 200 · L 220 · H 105 cm", "Lattenrost optional"),
      c("Kleiderschrank 4-türig", "Hoher Schrank mit Innenausstattung.", "FSC®-zertifizierter Holzwerkstoff, Echtholzfurnier, Wollfilz", "Räuchereiche · Sand", "B 240 · T 62 · H 220 cm", "2 Kleiderstangen, 4 Schübe"),
      c("Nachttische, Bronze (2 Stück)", "Nachttisch mit brünierter Bronze-Auflage.", "Massivholz, Bronze brüniert", "Eiche · Bronze", "B 50 · T 42 · H 58 cm", "Kabelausgang"),
      c("Kommode, breit", "Sechs-Schub-Kommode.", "FSC®-zertifizierter Holzwerkstoff, Echtholzfurnier", "Räuchereiche", "B 160 · T 48 · H 88 cm", "Vollauszüge"),
      c("Bank am Fußende", "Bank mit Wollfilz-Auflage.", "Massivholz, Wollfilz", "Räuchereiche · Anthrazit", "B 160 · T 42 · H 45 cm", "Auflage abnehmbar"),
      c("Pendelleuchten-Set (2 Stück)", "Zwei Pendel über Nachttische.", "Stahl, Opalglas", "Schwarz matt · Opal", "Ø 22 · H variabel", "LED E14 inklusive"),
      c("Textil-Paket", "Bettwäsche, Plaid, Vorhang, Teppich.", "Leinen, Wolle", "Taupe / Anthrazit", "Teppich 200 × 290 cm", "Schadstoffgeprüft"),
    ],
  },
  {
    slug: "essbereich-alpin",
    name: "Essbereich · Alpin",
    room: "essbereich",
    style: "alpin",
    style_label: "Alpin",
    subtitle: "Ehrlicher Tisch für gemeinsame Tage.",
    description:
      "Großer Esstisch aus Massivlärche mit Wollfilz-Stühlen — ein Essbereich, der Gespräche trägt und die Berge spürbar macht, ohne folkloristisch zu wirken.",
    suitedFor: ["Ferienhaus", "Refit", "Privat"],
    audience: "privat",
    priceFrom: 4490,
    priceTo: 5190,
    createdAt: "2026-02-02",
    image:
      "https://images.unsplash.com/photo-1592078615290-033ee584e267?auto=format&fit=crop&w=1600&q=80",
    palette: ["#EDE6D9", "#A89478", "#5C4A36", "#1A1816"],
    components: [
      c("Esstisch 200 × 95 cm", "Massiver Tisch mit weicher Kantenfase.", "Massive Lärche", "Lärche geräuchert", "B 200 · T 95 · H 76 cm", "Naturöl-Finish"),
      c("Stühle (6 Stück)", "Stuhl mit Wollfilz-Sitz.", "Massivholz, Wollfilz", "Lärche · Sand", "B 46 · T 50 · H 86 cm", "Auflage abnehmbar"),
      c("Sideboard 3-türig", "Aufbewahrung für Geschirr.", "Massivholz, Schmiedestahl", "Lärche · Schwarz matt", "B 200 · T 45 · H 80 cm", "Soft-Close"),
      c("Wandregal", "Wandregal über dem Sideboard.", "Massivholz", "Lärche", "B 160 · T 22 · H 30 cm", "Wandfixierung"),
      c("Pendelleuchte, 3-flammig", "Pendel über dem Esstisch.", "Stahl, Opalglas", "Schwarz matt · Opal", "B 110 · H variabel", "LED inklusive"),
      c("Geschirr- & Glas-Set", "Vollausstattung 8 Personen.", "Steinzeug, Glas", "Sand · Klar", "div.", "Spülmaschinenfest"),
    ],
  },
  {
    slug: "essbereich-urban-retreat",
    name: "Essbereich · Urban Retreat",
    room: "essbereich",
    style: "urban-retreat",
    style_label: "Urban Retreat",
    subtitle: "Konzentrierte Tafel für lange Abende.",
    description:
      "Esstisch aus Räuchereiche, Lederstühle, Bronze-Pendel: ein urbanes Esserlebnis mit dichter Materialität — ideal für Stadtapartments und Serviced-Formate.",
    suitedFor: ["Apartment", "Serviced Apartment", "Privat", "Gewerblich"],
    audience: "beide",
    priceFrom: 4790,
    priceTo: 5190,
    createdAt: "2026-01-30",
    image:
      "https://images.unsplash.com/photo-1615968679312-9b7ed9f04e79?auto=format&fit=crop&w=1600&q=80",
    palette: ["#EAE5DC", "#807568", "#3C352D", "#15130F"],
    components: [
      c("Esstisch 200 × 100 cm", "Tisch mit Räuchereiche-Platte und Stahlbeinen.", "Massive Eiche geräuchert, Stahl", "Räuchereiche · Schwarz matt", "B 200 · T 100 · H 76 cm", "Kantenfase 5°"),
      c("Stühle (6 Stück)", "Stuhl mit Pflanzleder-Bezug.", "Massivholz, Pflanzleder", "Eiche · Cognac", "B 48 · T 52 · H 84 cm", "Patiniert mit der Zeit"),
      c("Sideboard, breit", "Aufbewahrung mit Push-to-open Türen.", "Massivholz, Räuchereiche", "Räuchereiche", "B 200 · T 45 · H 80 cm", "Soft-Close"),
      c("Wandregal modular", "Modulares System, frei kombinierbar.", "Stahl, Räuchereiche", "Schwarz matt · Räuchereiche", "B 160 · T 25 · H 60 cm", "Wandfixierung"),
      c("Pendelleuchte, Bronze", "3-flammiges Pendel mit Bronze-Schirm.", "Bronze brüniert, Stahl", "Bronze · Schwarz matt", "B 110 · H variabel", "LED dimmbar"),
      c("Geschirr- & Glas-Set sand", "Vollausstattung 8 Personen.", "Steinzeug glasiert, Glas", "Sand · Klar", "div.", "Spülmaschinen- und mikrowellenfest"),
    ],
  },
  {
    slug: "bad-skandi",
    name: "Badmöbel · Skandi",
    room: "bad",
    style: "skandi",
    style_label: "Skandi",
    subtitle: "Helle Klarheit fürs Bad.",
    description:
      "Ein vollständiges Badmöbel-Set in skandinavischer Klarheit: matte Lackoberflächen, helle Esche, Messing brüniert. Funktioniert in Gäste-WCs ebenso wie in vollwertigen Hauptbädern.",
    suitedFor: ["Ferienwohnung", "Apartment", "Privat", "Gewerblich"],
    audience: "beide",
    priceFrom: 2490,
    priceTo: 3990,
    createdAt: "2026-02-08",
    image:
      "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=1600&q=80",
    palette: ["#F6F3ED", "#D9CFBE", "#8A8778", "#1F1E1C"],
    components: [
      c("Waschtisch-Unterschrank, 2-türig", "Wandhängender Unterschrank, grifflos.", "FSC®-zertifizierter Holzwerkstoff, Echtholzfurnier, Mineralguss", "Esche natur · Sand matt", "B 100 · T 48 · H 50 cm", "Inkl. Mineralguss-Becken"),
      c("Spiegel, beleuchtet", "Wandspiegel mit indirekter LED-Hinterleuchtung.", "Float-Glas, LED 2700 K", "Klar", "B 80 · H 80 cm", "Touch-Schalter, dimmbar"),
      c("Hochschrank, schmal", "Hoher Schrank mit zwei Türen.", "FSC®-zertifizierter Holzwerkstoff, Echtholzfurnier", "Esche natur", "B 40 · T 35 · H 200 cm", "5 Innenfächer"),
      c("Hängeregal", "Schmales Wandregal über dem Waschtisch.", "Massivholz", "Esche natur", "B 80 · T 18 · H 22 cm", "Wandfixierung"),
      c("Handtuchhalter", "Wandmontierter Halter, zweiteilig.", "Messing brüniert", "Messing matt", "B 60 cm", "Inkl. Befestigung"),
      c("Wandhaken-Set (3 Stück)", "Drei Doppelhaken auf Holzleiste.", "Massivholz, Messing", "Esche · Messing matt", "L 40 cm", "Doppelhaken"),
      c("Badteppich", "Weicher Frottee-Teppich.", "Baumwolle 100%", "Sand", "70 × 120 cm", "Bei 30 °C waschbar"),
    ],
  },
  {
    slug: "bad-kueste",
    name: "Badmöbel · Küste",
    room: "bad",
    style: "kueste",
    style_label: "Küste",
    subtitle: "Salz, Licht, leichtes Wasser.",
    description:
      "Badmöbel-Set für Ferienobjekte am Wasser: gebleichte Eiche, Rattan-Akzente, Messing brüniert. Verzeiht Sand und Sonnencreme — und sieht trotzdem aufgeräumt aus.",
    suitedFor: ["Ferienhaus", "Ferienwohnung", "Privat", "Gewerblich"],
    audience: "beide",
    priceFrom: 2790,
    priceTo: 3990,
    createdAt: "2026-02-10",
    image:
      "https://images.unsplash.com/photo-1620626011761-996317b8d101?auto=format&fit=crop&w=1600&q=80",
    palette: ["#EFE9DF", "#CDC2AE", "#8A8778", "#1F1E1C"],
    components: [
      c("Waschtisch, 2-türig", "Wandhängender Unterschrank mit Keramikbecken.", "Massive Eiche gekalkt, Keramik", "Eiche gekalkt · Weiß matt", "B 110 · T 48 · H 50 cm", "Inkl. Keramikbecken"),
      c("Spiegel, rund beleuchtet", "Runder Spiegel mit LED-Ring.", "Float-Glas, LED 2700 K", "Klar", "Ø 80 cm", "Dimmbar"),
      c("Hochschrank", "Hoher Schrank mit Glasfront.", "Massivholz, Float-Glas", "Eiche gekalkt", "B 45 · T 35 · H 200 cm", "5 Innenfächer"),
      c("Standregal Rattan", "Standregal mit Rattan-Türen.", "Massivholz, Naturrattan", "Eiche gekalkt · Natur", "B 60 · T 35 · H 180 cm", "Handgeflochten"),
      c("Handtuchleiter", "Frei stehende Handtuchleiter.", "Massive Eiche", "Eiche gekalkt", "B 50 · H 170 cm", "Vier Sprossen"),
      c("Wandhaken Messing", "Drei Wandhaken auf Holzleiste.", "Massivholz, Messing brüniert", "Eiche · Messing", "L 50 cm", "Inkl. Befestigung"),
      c("Textil-Set", "Badteppich, 4 Handtücher, Duschvorhang.", "Baumwolle 100%", "Natur / Sand", "Teppich 70 × 120 cm", "Bei 30 °C waschbar"),
    ],
  },
  {
    slug: "bad-urban-retreat",
    name: "Badmöbel · Urban Retreat",
    room: "bad",
    style: "urban-retreat",
    style_label: "Urban Retreat",
    subtitle: "Dichte Materialität fürs Stadt-Bad.",
    description:
      "Ein dichtes, urbanes Badmöbel-Set: Räuchereiche, brünierte Bronze, schwarze Mattflächen. Premium-Format für Apartmentanlagen, Boutique-Hotels und Serviced-Bäder.",
    suitedFor: ["Apartment", "Serviced Apartment", "Privat", "Gewerblich"],
    audience: "beide",
    priceFrom: 3290,
    priceTo: 3990,
    createdAt: "2026-02-12",
    image:
      "https://images.unsplash.com/photo-1604709177225-055f99402ea3?auto=format&fit=crop&w=1600&q=80",
    palette: ["#EAE5DC", "#807568", "#3C352D", "#15130F"],
    components: [
      c("Doppel-Waschtisch", "Wandhängender Unterschrank mit zwei Becken.", "FSC®-zertifizierter Holzwerkstoff, Echtholzfurnier, Mineralguss", "Räuchereiche · Anthrazit matt", "B 140 · T 50 · H 50 cm", "Inkl. zwei Mineralguss-Becken"),
      c("Spiegelschrank LED", "Spiegelschrank mit LED-Hinterleuchtung.", "Stahl, Spiegel, LED 2700 K", "Schwarz matt", "B 140 · T 16 · H 75 cm", "Touch-Schalter, dimmbar"),
      c("Hochschrank", "Hoher Schrank mit drei Türen.", "FSC®-zertifizierter Holzwerkstoff, Echtholzfurnier", "Räuchereiche", "B 50 · T 35 · H 220 cm", "Soft-Close"),
      c("Hängeregal Stahl", "Wandregal mit Stahlrahmen.", "Stahl pulverbeschichtet, Räuchereiche", "Schwarz matt · Räuchereiche", "B 100 · T 22 · H 30 cm", "Wandfixierung im Set"),
      c("Handtuchsystem", "Wandsystem mit Halter und Haken.", "Bronze brüniert", "Bronze matt", "B 80 cm", "Inkl. Befestigung"),
      c("Wandhaken-Set (3 Stück)", "Drei Doppelhaken aus Schwarzstahl.", "Stahl pulverbeschichtet", "Schwarz matt", "div.", "Doppelhaken"),
      c("Textil-Set", "Badteppich, 6 Handtücher, Duschvorhang.", "Baumwolle 100%", "Anthrazit / Taupe", "Teppich 80 × 140 cm", "Bei 40 °C waschbar"),
    ],
  },
];

// Convenience: find a set by slug.
export const findRoomSet = (slug) => ROOM_SETS.find((s) => s.slug === slug);

// Sort helper.
export const sortRoomSets = (list, mode) => {
  const arr = [...list];
  switch (mode) {
    case "price-asc":
      return arr.sort((a, b) => a.priceFrom - b.priceFrom);
    case "price-desc":
      return arr.sort((a, b) => b.priceFrom - a.priceFrom);
    case "newest":
      return arr.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    case "recommended":
    default:
      return arr;
  }
};
